import { useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import {
  createAsaasCustomer, createPixAuthorization, createSubscription, fetchPlanConfig,
  fetchSubscriptionStatus, pollPaymentStatus, clearError, type BillingCycle,
} from '@maestra/core/store/slices/subscription';
import { precoDaAssinatura, precoMensalizado } from '@maestra/core/nucleo/precoDaAssinatura';
import { useCheckoutForm } from '@maestra/core/hooks/useCheckoutForm';
import { useCoupon } from '@maestra/core/hooks/useCoupon';

// O CHECKOUT DA ASSINATURA, sem tela. Cópia literal de `pages/Subscription/CheckoutDaAssinatura`:
// o mesmo cálculo que a edge usa (`precoDaAssinatura`), os mesmos add-ons (o seletor mostra o
// TOTAL, o estado guarda o EXTRA), o cupom, e a mesma ordem da cobrança: cliente na Asaas → Pix
// Automático (quando ligado) → assinatura por cobrança; no cartão, a espera da operadora antes de
// liberar o PRO. A /planos e o desbloqueio de perfil montam este mesmo checkout.

const FALLBACK_MONTHLY = 57.9;
export const fmtBRL = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const addMonths = (n: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

export type MetodoDePagamento = 'PIX' | 'CREDIT';

export interface LinhaDeAdicional {
  id: 'perfis' | 'armazenamento';
  titulo: string;
  incluso: number;
  total: number;
  mudar: (total: number) => void;
  max: number;
  passo: number;
  unidade: [string, string];
  /** "1 perfil incluso + 2 perfis · R$ 55,80/mês", pronto. */
  resumo: string;
  /** Um aviso (perfis que ficariam inacessíveis) ou só uma informação (quanto se usa hoje). */
  nota?: { texto: string; alerta: boolean };
}

/** A linha do seletor de um adicional: o que o plano inclui, o que se acrescenta e quanto custa. */
export const linhaDoAdicional = (l: Omit<LinhaDeAdicional, 'resumo'> & { valorUnitario: number }, anual: boolean): LinhaDeAdicional => {
  const escrever = (n: number) => `${n} ${n === 1 ? l.unidade[0] : l.unidade[1]}`;
  const adicionais = Math.max(0, l.total - l.incluso);
  const { valorUnitario, ...resto } = l;
  return {
    ...resto,
    resumo: adicionais === 0
      ? `${escrever(l.incluso)}, incluso no plano`
      : `${escrever(l.incluso)} incluso${l.incluso === 1 ? '' : 's'} + ${escrever(adicionais)} · ${fmtBRL(adicionais * valorUnitario)}/mês${anual ? ', cobrado no total do ano' : ''}`,
  };
};

export const useCheckoutDaAssinatura = ({ aoAtivar, aoIrParaPagamento, aoJaSerAssinante, cicloInicial, perfisIniciais, gbIniciais }: {
  aoAtivar: () => void;
  aoIrParaPagamento: () => void;
  aoJaSerAssinante: () => void;
  /** O ciclo já escolhido na vitrine (sem ele, quem escolheu anual contratava mensal). */
  cicloInicial?: BillingCycle;
  /** As quantidades de antes, quando a pessoa volta de "Alterar plano ou pagamento". */
  perfisIniciais?: number;
  gbIniciais?: number;
}) => {
  const dispatch = useAppDispatch();
  const { loading, error, plan, profilesInQuota } = useAppSelector((s) => s.subscription);
  const user = useAppSelector((s) => s.auth.user);

  const userEmail = user?.email || '';
  // A Asaas exige ao menos 3 letras no nome: o pedaço do e-mail antes do @ ("ab@...") travava o
  // checkout para sempre, sem campo para corrigir (QA do pagamento). Curto, vai o e-mail inteiro.
  const nomeDaConta = String(user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '').trim();
  const userName = nomeDaConta.length >= 3 ? nomeDaConta : userEmail;

  const [method, setMethod] = useState<MetodoDePagamento>('PIX');
  const [cycle, setCycle] = useState<BillingCycle>(cicloInicial ?? 'MONTHLY');
  const [extraProfiles, setExtraProfiles] = useState(perfisIniciais ?? 0);
  const [extraStorageGb, setExtraStorageGb] = useState(gbIniciais ?? 0);
  const [formError, setFormError] = useState('');
  const [confirmingCard, setConfirmingCard] = useState(false);
  // A espera do cartão em análise para ao sair da tela: antes ela seguia e, minutos depois,
  // navegava sozinha para o sucesso ou o planejamento (QA do pagamento, 04/10/2026).
  const esperaDoCartao = useRef<{ abort: () => void } | null>(null);
  const montado = useRef(true);
  useEffect(() => { montado.current = true; return () => { montado.current = false; esperaDoCartao.current?.abort(); }; }, []);
  const form = useCheckoutForm();
  const coupon = useCoupon();

  const isCard = method === 'CREDIT';
  const isPix = method === 'PIX';

  useEffect(() => {
    dispatch(fetchPlanConfig());
    dispatch(fetchSubscriptionStatus());
  }, [dispatch]);

  const monthlyValue = plan?.monthlyValue ?? FALLBACK_MONTHLY;
  const annualValue = plan?.annualValue ?? null;
  const annualEnabled = !!(plan?.annualEnabled && annualValue && annualValue > 0);
  const isAnnual = cycle === 'YEARLY' && annualEnabled;

  const composicao = {
    ciclo: (isAnnual ? 'YEARLY' : 'MONTHLY') as BillingCycle,
    mensal: monthlyValue,
    anual: annualEnabled ? annualValue : null,
    perfisExtras: extraProfiles,
    gbExtras: extraStorageGb,
    valorPorPerfil: plan?.profileAddonValue ?? 27.9,
    valorPorGb: plan?.storageAddonValuePerGb ?? 7.9,
  };
  const currentValue = precoDaAssinatura(composicao);
  const totalDoMesmoPlanoMensal = precoDaAssinatura({ ...composicao, ciclo: 'MONTHLY' }) * 12;
  const economiaAnual = Math.max(0, totalDoMesmoPlanoMensal - currentValue);
  const discountPct = annualValue ? Math.round((1 - annualValue / (monthlyValue * 12)) * 100) : 0;
  const perfisInclusos = plan?.includedProfiles ?? 1;
  const gbInclusos = plan?.includedStorageGb ?? 30;
  const cycleLabel = isAnnual ? 'Anual' : 'Mensal';
  const unit = isAnnual ? '/ano' : '/mês';
  const priceFmt = fmtBRL(currentValue);
  const { amount: couponDiscount, final: totalValue } = coupon.discountFor(currentValue);
  const totalFmt = fmtBRL(totalValue);
  const everyWord = isAnnual ? 'a cada 12 meses' : 'a cada mês';
  const pixAutomatico = isPix && !!plan?.pixAutomaticEnabled;
  // O mínimo de R$ 5 com cupom só era conferido ao aplicar: trocar o anual pelo mensal depois
  // deixava o total abaixo do mínimo, e o servidor recusava num aviso genérico (QA do pagamento).
  const cupomAbaixoDoMinimo = !!coupon.applied && totalValue < 5;
  const avisoDoMinimo = 'Com este cupom o valor fica abaixo do mínimo de R$ 5,00. Troque o cupom ou o plano.';

  const handleSubmit = async () => {
    const err = form.validate(isCard);
    if (err) { form.markSubmitted(isCard); return false; }
    if (cupomAbaixoDoMinimo) { setFormError(avisoDoMinimo); return false; }
    setFormError('');
    dispatch(clearError());

    const rawCpfCnpj = form.cpf.replace(/\D/g, '');
    const rawPhone = form.phone.replace(/\D/g, '');
    const customerResult = await dispatch(createAsaasCustomer({
      name: userName, email: userEmail, cpfCnpj: rawCpfCnpj,
      ...(rawPhone ? { mobilePhone: rawPhone } : {}),
    }));
    if (createAsaasCustomer.rejected.match(customerResult)) {
      setFormError((customerResult.payload as string) || 'Erro ao iniciar a cobrança.');
      return true;
    }
    const customerId = (customerResult.payload as { customerId: string }).customerId;
    const couponCode = coupon.couponCode ?? undefined;

    if (method === 'PIX') {
      if (plan?.pixAutomaticEnabled) {
        const auth = await dispatch(createPixAuthorization({
          customerId, cycle: composicao.ciclo, couponCode, extraProfiles, extraStorageGb,
        }));
        if (createPixAuthorization.fulfilled.match(auth)) {
          const a = auth.payload as { resume?: boolean; alreadyActive?: boolean; pixData?: { qrCode: string | null } | null };
          if (a.alreadyActive) { aoJaSerAssinante(); return true; }
          if (a.resume || a.pixData?.qrCode) { aoIrParaPagamento(); return true; }
        }
        console.warn('Pix Automático indisponível; seguindo pelo fluxo de cobrança por ciclo.');
      }

      const result = await dispatch(createSubscription({
        customerId, billingType: 'PIX', cycle: composicao.ciclo, couponCode, extraProfiles, extraStorageGb,
      }));
      if (createSubscription.rejected.match(result)) return true;
      const p = result.payload as { resume?: boolean; alreadyActive?: boolean };
      if (p.alreadyActive) { aoJaSerAssinante(); return true; }
      if (p.resume) { aoIrParaPagamento(); return true; }
      const pixPayload = (result.payload as { pixData?: { qrCode: string | null } | null })?.pixData;
      if (!pixPayload?.qrCode) return true;
      aoIrParaPagamento();
    } else {
      const expiryDigits = form.cardExpiry.replace(/\D/g, '');
      const result = await dispatch(createSubscription({
        customerId,
        billingType: 'CREDIT_CARD',
        // O ciclo que a tela mostra (o anual desligado depois da escolha cai no mensal), e não o
        // estado cru, que mandava "anual" e era recusado sem saída (QA do pagamento).
        cycle: composicao.ciclo,
        couponCode,
        extraProfiles,
        extraStorageGb,
        creditCard: {
          holderName: form.cardName.trim(),
          number: form.cardNumber.replace(/\D/g, ''),
          expiryMonth: expiryDigits.slice(0, 2),
          expiryYear: '20' + expiryDigits.slice(2, 4),
          ccv: form.cardCvv,
        },
        creditCardHolderInfo: {
          name: userName, email: userEmail, cpfCnpj: rawCpfCnpj,
          postalCode: form.cep.replace(/\D/g, ''), phone: form.phone.replace(/\D/g, ''),
          // O número do endereço digitado (B2): ia sempre "0".
          addressNumber: form.addressNumber.trim(),
          ...(form.resolvedAddress ? { address: form.resolvedAddress.logradouro, province: form.resolvedAddress.bairro, city: form.resolvedAddress.localidade, uf: form.resolvedAddress.uf } : {}),
        },
      }));
      if (createSubscription.rejected.match(result)) return true;
      const p = result.payload as { resume?: boolean; alreadyActive?: boolean; status?: string };
      if (p.alreadyActive) { aoJaSerAssinante(); return true; }
      if (p.resume) { aoIrParaPagamento(); return true; }
      if (p.status === 'active') { aoAtivar(); return true; }

      setConfirmingCard(true);
      const espera = dispatch(pollPaymentStatus());
      esperaDoCartao.current = espera;
      const poll = await espera;
      if (!montado.current) return true;
      setConfirmingCard(false);
      if (pollPaymentStatus.fulfilled.match(poll)) { aoAtivar(); return true; }
      setFormError('Pagamento em análise pela operadora do cartão. Seu acesso PRO será liberado automaticamente assim que for aprovado.');
    }
    return true;
  };

  const adicionais: LinhaDeAdicional[] = [
    linhaDoAdicional({
      id: 'perfis',
      titulo: 'Perfis de artista',
      incluso: perfisInclusos,
      total: perfisInclusos + extraProfiles,
      mudar: (total) => setExtraProfiles(Math.max(0, total - perfisInclusos)),
      max: perfisInclusos + 50,
      passo: 1,
      valorUnitario: composicao.valorPorPerfil,
      unidade: ['perfil', 'perfis'],
      nota: profilesInQuota > perfisInclusos + extraProfiles
        ? { alerta: true, texto: `Você tem ${profilesInQuota} perfis. Sem mais ${profilesInQuota - perfisInclusos - extraProfiles}, os excedentes ficam inacessíveis (nada é apagado).` }
        : undefined,
    }, isAnnual),
    linhaDoAdicional({
      id: 'armazenamento',
      titulo: 'Armazenamento',
      incluso: gbInclusos,
      total: gbInclusos + extraStorageGb,
      mudar: (total) => setExtraStorageGb(Math.max(0, total - gbInclusos)),
      max: gbInclusos + 2000,
      passo: 10,
      valorUnitario: composicao.valorPorGb,
      unidade: ['GB', 'GB'],
    }, isAnnual),
  ];

  const nPerfis = perfisInclusos + extraProfiles;

  return {
    email: userEmail,
    form,
    metodo: { valor: method, mudar: setMethod },
    ciclo: annualEnabled ? {
      valor: (isAnnual ? 'YEARLY' : 'MONTHLY') as BillingCycle,
      mudar: setCycle,
      rotuloDoAnual: `Anual${discountPct > 0 ? ` · -${discountPct}%` : ''}`,
    } : null,
    adicionais,
    cupom: {
      valor: coupon.input,
      mudar: coupon.setInput,
      aplicar: () => coupon.apply('subscription', currentValue),
      tirar: coupon.clear,
      validando: coupon.loading,
      erro: coupon.error || null,
      aplicado: coupon.labelFor(currentValue),
    },
    item: {
      nome: 'Maestra PRO',
      detalhe: `${nPerfis} perfil${nPerfis > 1 ? 's' : ''} · ${gbInclusos + extraStorageGb} GB${isAnnual ? ` · equivale a ${fmtBRL(precoMensalizado(composicao))}/mês` : ` · ${cycleLabel}`}`,
      preco: `${priceFmt}${unit}`,
    },
    linhas: [
      // No anual, o subtotal é o preço de 12 meses avulsos: a economia sai DELE, e a conta fecha
      // (antes o subtotal já vinha com desconto, e Subtotal menos Economia não dava o Total).
      { rotulo: 'Subtotal', valor: isAnnual && economiaAnual > 0 ? `${fmtBRL(totalDoMesmoPlanoMensal)}${unit}` : `${priceFmt}${unit}` },
      ...(isAnnual && economiaAnual > 0 ? [{ rotulo: `Economia (${discountPct}%)`, valor: `−${fmtBRL(economiaAnual)}` }] : []),
      ...(coupon.applied ? [{ rotulo: `Cupom ${coupon.applied.code}`, valor: `−${fmtBRL(couponDiscount)}` }] : []),
    ],
    total: { rotulo: 'Total hoje', valor: totalFmt },
    // PIX hoje é Pix Automático (a flag do plano): pagar o QR autoriza o débito dos próximos ciclos.
    // O texto dizia "uma nova cobrança PIX a cada mês", que é o outro produto (QA do pagamento).
    renovacao: {
      rotulo: `${pixAutomatico ? 'Próximo débito em' : isPix ? 'Próxima cobrança em' : 'Renova em'} ${isAnnual ? addMonths(12) : addMonths(1)}`,
      valor: `${totalFmt}${unit}`,
      apoio: pixAutomatico
        ? `Débito automático ${everyWord} na sua conta, pelo Pix Automático. Cancele quando quiser pela sua conta ou pelo seu banco.`
        : isPix
          ? `Geramos uma nova cobrança PIX ${everyWord}. Cancele quando quiser pela sua conta.`
          : `Renova automaticamente${isAnnual ? ' ' + everyWord : ''}. Cancele quando quiser pela sua conta.`,
    },
    legal: pixAutomatico
      ? `Ao pagar o QR, você autoriza o débito automático de ${totalFmt} ${isAnnual ? 'por ano' : 'por mês'} na sua conta enquanto a assinatura estiver ativa. Cancele quando quiser pela sua conta ou pelo seu banco.`
      : isPix
        ? `Você receberá uma cobrança PIX de ${totalFmt} ${everyWord} enquanto a assinatura estiver ativa. Cancele quando quiser pela sua conta.`
        : `Ao assinar, você autoriza a cobrança automática de ${totalFmt} ${isAnnual ? 'por ano' : 'por mês'} até cancelar. Cancele quando quiser pela sua conta.`,
    botao: isPix ? 'Pagar com PIX' : 'Concordar e assinar',
    enviando: loading || confirmingCard,
    /** O que falta preencher: a tela diz embaixo do botão, antes do clique. */
    falta: form.validate(isCard) || (cupomAbaixoDoMinimo ? avisoDoMinimo : null),
    erro: formError || error || null,
    /** Paga; devolve `false` quando o formulário tem campo a corrigir (a tela foca o primeiro). */
    pagar: handleSubmit,
  };
};

export type DadosDoCheckout = ReturnType<typeof useCheckoutDaAssinatura>;
